import React from "react";
import ImageUpload from "@/admin/ImageUpload";
import { X, Plus, GripVertical } from "lucide-react";

/**
 * Rich photo array — each item = { url, caption }. Backward compatible with string-only arrays.
 * Used on crews.photos so each shot carries its own comp name / year / placement.
 */
export default function MultiPhotoUpload({ value, onChange, label = "Photos", testId = "photos" }) {
  const arr = Array.isArray(value)
    ? value.map(v => (typeof v === "string" ? { url: v, caption: "" } : { url: v?.url || "", caption: v?.caption || "", _key: v?._key }))
    : [];
  const keyFor = (row, i) => row._key || `${row.url || "empty"}-${i}`;
  const set = (i, key, v) => { const next = arr.map((r, idx) => idx === i ? { ...r, [key]: v } : r); onChange(next); };
  const add = () => onChange([...arr, { url: "", caption: "", _key: `p-${Date.now()}` }]);
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= arr.length) return;
    const next = [...arr]; [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="eyebrow">{label} ({arr.length})</label>
        <button type="button" onClick={add} className="text-xs text-red-500 hover:text-red-400 uppercase tracking-widest flex items-center gap-1" data-testid={`${testId}-add`}>
          <Plus size={12}/> Add photo
        </button>
      </div>
      {arr.length === 0 && (
        <div className="text-zinc-600 text-xs py-3 border border-dashed border-white/10 text-center">
          No photos yet — click "Add photo" to build a highlights reel.
        </div>
      )}
      <div className="space-y-3">
        {arr.map((row, i) => (
          <div key={keyFor(row, i)} className="flex gap-2 items-start p-3 border border-white/5" data-testid={`${testId}-row-${i}`}>
            <div className="flex flex-col gap-1 pt-6 text-zinc-500">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-[10px] disabled:opacity-30 hover:text-white">▲</button>
              <GripVertical size={14}/>
              <button type="button" onClick={() => move(i, +1)} disabled={i === arr.length - 1} className="text-[10px] disabled:opacity-30 hover:text-white">▼</button>
            </div>
            <div className="flex-1 space-y-2">
              <ImageUpload label={`Photo #${i+1}`} value={row.url} onChange={v => set(i, "url", v)} testId={`${testId}-${i}`} />
              <input type="text" placeholder="Caption — e.g. Melbourne Open · 2025 · 1st Place"
                value={row.caption}
                onChange={e => set(i, "caption", e.target.value)}
                className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none"
                data-testid={`${testId}-caption-${i}`}
              />
            </div>
            <button type="button" onClick={() => remove(i)} className="text-zinc-400 hover:text-red-500 p-2 mt-6" title="Remove" data-testid={`${testId}-remove-${i}`}>
              <X size={16}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
